import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/RateReviewOutlined';
import { AppBar, Toolbar, Typography, Button, IconButton, Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { usePermissions } from '../../hooks/permissions/usePermissions';
import { useSystem } from '../../hooks/api/config';

/** Feedback form for the pilot phase (external, opens in a new tab). */
const FEEDBACK_URL = 'https://smarty-feedback.cprogruppe.chatgpt.site/';

interface LayoutAppBarProps {
  user: any;
  isMobile: boolean;
  onMenuClick: () => void;
  onLogout: () => void;
}

export const LayoutAppBar: React.FC<LayoutAppBarProps> = ({
  user,
  isMobile,
  onMenuClick,
  onLogout
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { system } = useSystem();
  const { can } = usePermissions();

  const handleSystemSettings = () => {
    navigate('/system-management');
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* System Name with optional custom branding */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="div">
            {system?.name || 'Smarty'}
          </Typography>

          {/* Environment badge (system description), hidden on mobile */}
          {!isMobile && system?.description && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.375,
                borderRadius: 999,
                border: '1px solid rgba(255, 255, 255, 0.28)',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
              }}
            >
              <Box
                component="span"
                aria-hidden
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#ffb74d',
                  boxShadow: '0 0 0 3px rgba(255, 183, 77, 0.25)',
                }}
              />
              {system.description}
            </Box>
          )}
        </Box>

        {/* Feedback: left of the gear for administrators, left of the logout
            button for everyone else. Header only, not in the navigation. */}
        {!isMobile && (
          <Button
            color="inherit"
            component="a"
            href={FEEDBACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<FeedbackIcon />}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            {t('user.feedback')}
          </Button>
        )}

        {/* Admin System Settings Button */}
        {can.configureChatModels && !isMobile && (
          <IconButton
            color="inherit"
            onClick={handleSystemSettings}
            sx={{ mr: 1 }}
            title={t('system.settings')}
          >
            <SettingsIcon />
          </IconButton>
        )}
        
        {/* Logout Button */}
        {!isMobile && (
          <Button color="inherit" onClick={onLogout}>
            {t('user.logout')}
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};